import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, message, Collapse, Row, Col, Popconfirm, Descriptions } from 'antd';
import { agentApi, type AgentDTO } from '../api/kfc';
import { get56yorkyApiByName, getApiPath } from '../data/56yorkyApiCollection';
import ResponsiveDrawer from '../components/ResponsiveDrawer';

/** 单个能力 + API 详情卡片（含 56yorky 文档时展示 URL、参数、文档） */
const CapabilityApiCard: React.FC<{
  capability: string;
  apiName: string;
  agentIdentifier?: string;
}> = ({ capability, apiName, agentIdentifier }) => {
  const apiDoc = agentIdentifier === '56yorky' ? get56yorkyApiByName(apiName) : undefined;
  const path = apiDoc ? getApiPath(apiDoc) : '';
  const req = apiDoc?.request;

  return (
    <div style={{ marginBottom: 20, padding: 16, background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0' }}>
      <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 15 }}>
        {capability}
        <span style={{ fontWeight: 400, color: '#666', marginLeft: 8 }}>→ {apiName}</span>
      </div>
      {apiDoc && req ? (
        <>
          <Descriptions column={1} size="small" style={{ marginBottom: 12 }}>
            <Descriptions.Item label="请求方式">{req.method}</Descriptions.Item>
            <Descriptions.Item label="API 路径">{path || req.url?.raw || '-'}</Descriptions.Item>
          </Descriptions>
          {req.header && req.header.length > 0 && (
            <>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>请求头</div>
              <Table
                size="small"
                dataSource={req.header}
                rowKey="key"
                pagination={false}
                columns={[
                  { title: 'Key', dataIndex: 'key', width: 140 },
                  { title: 'Value/说明', dataIndex: 'value', ellipsis: true },
                ]}
                style={{ marginBottom: 12 }}
              />
            </>
          )}
          {req.url?.query && req.url.query.length > 0 && (
            <>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Query 参数</div>
              <Table
                size="small"
                dataSource={req.url.query}
                rowKey="key"
                pagination={false}
                columns={[
                  { title: '参数', dataIndex: 'key', width: 120 },
                  { title: '说明', dataIndex: 'description', ellipsis: true },
                ]}
                style={{ marginBottom: 12 }}
              />
            </>
          )}
          {req.body?.raw && (
            <>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>请求体示例</div>
              <pre style={{ background: '#fff', padding: 8, borderRadius: 4, fontSize: 12, overflow: 'auto', maxHeight: 160 }}>{req.body.raw}</pre>
            </>
          )}
          {req.description && (
            <>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>文档说明</div>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, fontSize: 12, lineHeight: 1.5 }}>{req.description}</pre>
            </>
          )}
        </>
      ) : (
        <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>暂无该 API 的详细文档</div>
      )}
    </div>
  );
};

const AgentManage: React.FC = () => {
  const [list, setList] = useState<AgentDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form] = Form.useForm();
  const [capabilitiesDrawerOpen, setCapabilitiesDrawerOpen] = useState(false);
  const [capabilitiesAgent, setCapabilitiesAgent] = useState<AgentDTO | null>(null);

  const load = () => {
    setLoading(true);
    agentApi.list()
      .then((res) => setList(res.data))
      .catch(() => { setList([]); message.warning('无法加载代理列表'); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({
      basicInfo: {},
      contact: { contactPerson: {} },
      officialWebsite: {},
      apiConfig: {},
      bankAccount: {},
    });
    setModalOpen(true);
  };

  const openEdit = (r: AgentDTO) => {
    setEditingId(r.id ?? null);
    form.setFieldsValue({
      basicInfo: r.basicInfo ?? {},
      contact: {
        contactPerson: r.contact?.contactPerson ?? {},
        address: r.contact?.address,
      },
      officialWebsite: r.officialWebsite ?? {},
      apiConfig: r.apiConfig ?? {},
      bankAccount: r.bankAccount ?? {},
    });
    setModalOpen(true);
  };

  const onFinish = (values: Record<string, any>) => {
    const dto: Partial<AgentDTO> = {
      basicInfo: values.basicInfo,
      contact: values.contact,
      officialWebsite: values.officialWebsite,
      apiConfig: values.apiConfig,
      bankAccount: values.bankAccount,
    };
    const submit = editingId
      ? agentApi.update(editingId, dto)
      : agentApi.create(dto);
    submit.then(() => {
      message.success(editingId ? '更新成功' : '创建成功');
      setModalOpen(false);
      load();
    }).catch((e) => message.error(e.response?.data?.message || '操作失败'));
  };

  const testConn = (id: string) => {
    agentApi.testConnection(id).then((res) => {
      message.success(res.data.success ? '连接成功' : '连接失败');
    }).catch(() => message.error('测试失败'));
  };

  const onDelete = (id: string) => {
    agentApi.delete(id)
      .then(() => { message.success('删除成功'); load(); })
      .catch((e) => message.error(e.response?.data?.message || '删除失败'));
  };

  const openCapabilities = (r: AgentDTO) => {
    setCapabilitiesAgent(r);
    setCapabilitiesDrawerOpen(true);
  };

  return (
    <>
      <Card title="代理列表" extra={<Button type="primary" onClick={openAdd}>新增代理</Button>}>
        <Table
          loading={loading}
          dataSource={list}
          rowKey="id"
          scroll={{ x: 1100 }}
          columns={[
            { title: '代理名称', dataIndex: ['basicInfo', 'name'], width: 120, ellipsis: true },
            { title: '代理标识', dataIndex: ['basicInfo', 'identifier'], width: 120, ellipsis: true },
            { title: '对接人', dataIndex: ['contact', 'contactPerson', 'name'], width: 100, ellipsis: true },
            { title: '对接人电话', dataIndex: ['contact', 'contactPerson', 'phone'], width: 120, ellipsis: true },
            { title: '地址', dataIndex: ['contact', 'address'], width: 150, ellipsis: true },
            { title: '官网地址', dataIndex: ['officialWebsite', 'url'], width: 150, ellipsis: true },
            {
              title: '操作',
              width: 280,
              fixed: 'right',
              render: (_, r) => (
                <>
                  <Button type="link" size="small" onClick={() => openEdit(r)}>编辑</Button>
                  <Button type="link" size="small" onClick={() => openCapabilities(r)}>查看代理能力</Button>
                  <Button type="link" size="small" onClick={() => r.id && testConn(r.id)}>测试连接</Button>
                  <Popconfirm
                    title="确定要删除该代理吗？其下关联的渠道也会被删除。"
                    onConfirm={() => r.id && onDelete(r.id)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button type="link" size="small" danger>删除</Button>
                  </Popconfirm>
                </>
              ),
            },
          ]}
        />
      </Card>
      <Modal
        title={editingId ? '编辑代理' : '新增代理'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={720}
        bodyStyle={{ maxHeight: '70vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <Form form={form} layout="vertical" onFinish={onFinish} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
          <div style={{ overflowY: 'auto', flex: 1, marginRight: -8, paddingRight: 8 }}>
            <Collapse
              defaultActiveKey={['basicInfo', 'contact', 'officialWebsite', 'apiConfig', 'bankAccount']}
              items={[
              {
                key: 'basicInfo',
                label: '基本信息',
                children: (
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name={['basicInfo', 'name']} label="代理名称" rules={[{ required: true, message: '请输入代理名称' }]}>
                        <Input placeholder="代理名称*" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={['basicInfo', 'identifier']} label="代理标识" rules={[{ required: true, message: '请输入代理标识' }]}>
                        <Input placeholder="代理标识*" disabled={!!editingId} />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                key: 'contact',
                label: '联系信息',
                children: (
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name={['contact', 'contactPerson', 'name']} label="对接人名称" rules={[{ required: true, message: '请输入对接人名称' }]}>
                        <Input placeholder="对接人名称*" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={['contact', 'contactPerson', 'phone']} label="对接人电话" rules={[{ required: true, message: '请输入对接人电话' }]}>
                        <Input placeholder="对接人电话*" />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item name={['contact', 'address']} label="地址">
                        <Input placeholder="地址" />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                key: 'officialWebsite',
                label: '官网',
                children: (
                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item name={['officialWebsite', 'url']} label="官网地址" rules={[{ required: true, message: '请输入官网地址' }]}>
                        <Input placeholder="官网地址*" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={['officialWebsite', 'username']} label="用户名">
                        <Input placeholder="用户名" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={['officialWebsite', 'password']} label="密码">
                        <Input.Password placeholder="密码" autoComplete="new-password" />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                key: 'apiConfig',
                label: 'API 配置',
                children: (
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name={['apiConfig', 'priceEndPoint']} label="查询价格端点">
                        <Input placeholder="查询价格端点" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={['apiConfig', 'trackEndPoint']} label="查询轨迹端点">
                        <Input placeholder="查询轨迹端点" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={['apiConfig', 'logisticsSystemEndPoint']} label="物流系统对接端点">
                        <Input placeholder="物流系统对接端点" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={['apiConfig', 'appKey']} label="appkey">
                        <Input placeholder="appkey" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={['apiConfig', 'appSecret']} label="appsecret">
                        <Input.Password placeholder="appsecret" autoComplete="new-password" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={['apiConfig', 'coid']} label="coid（哲盟Q5）">
                        <Input placeholder="如 DEVCN" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={['apiConfig', 'nonce']} label="nonce（哲盟Q5）">
                        <Input placeholder="默认 slnkda" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={['apiConfig', 'version']} label="version（哲盟Q5）">
                        <Input placeholder="默认 1.0" />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                key: 'bankAccount',
                label: '银行账户',
                children: (
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name={['bankAccount', 'bankName']} label="开户行">
                        <Input placeholder="开户行" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={['bankAccount', 'accountName']} label="开户名">
                        <Input placeholder="开户名" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={['bankAccount', 'accountNumber']} label="银行账号">
                        <Input placeholder="银行账号" />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
            ]}
            />
          </div>
          <Form.Item style={{ marginTop: 16, marginBottom: 0, flexShrink: 0 }}>
            <Button type="primary" htmlType="submit">保存</Button>
            <Button style={{ marginLeft: 8 }} onClick={() => setModalOpen(false)}>取消</Button>
          </Form.Item>
        </Form>
      </Modal>
      <ResponsiveDrawer
        title={`代理能力 - ${capabilitiesAgent?.basicInfo?.name ?? capabilitiesAgent?.basicInfo?.identifier ?? ''}`}
        size="md"
        open={capabilitiesDrawerOpen}
        onClose={() => { setCapabilitiesDrawerOpen(false); setCapabilitiesAgent(null); }}
        closable
        footer={null}
      >
        {capabilitiesAgent && (
          <>
            {capabilitiesAgent.capabilities?.length ? (
              <div style={{ paddingRight: 8 }}>
                {capabilitiesAgent.capabilities.map((cap, i) => (
                  <CapabilityApiCard
                    key={i}
                    capability={cap.capability ?? ''}
                    apiName={cap.apiName ?? ''}
                    agentIdentifier={capabilitiesAgent.basicInfo?.identifier}
                  />
                ))}
              </div>
            ) : (
              <div style={{ color: 'rgba(0,0,0,0.45)' }}>暂无配置的代理能力映射</div>
            )}
          </>
        )}
      </ResponsiveDrawer>
    </>
  );
};

export default AgentManage;
